import React from 'react';
import styled from 'styled-components';
import {
  useApprovePendingBusiness,
  usePendingBusinesses,
  useRejectPendingBusiness,
} from '../hooks/usePendingBusinesses';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0 0 1.5rem 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ApproveButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font: inherit;
  font-weight: 600;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  border: 1px solid var(--error);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font: inherit;
  font-weight: 600;
  background: transparent;
  color: var(--error);
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  color: var(--error);
`;

const normalizeWebsite = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const formatSubmittedAt = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const SubmittedBusinessesPage: React.FC = () => {
  const { data: businesses, isLoading, error } = usePendingBusinesses();
  const approveMutation = useApprovePendingBusiness();
  const rejectMutation = useRejectPendingBusiness();

  const busyId = approveMutation.isPending
    ? approveMutation.variables
    : rejectMutation.isPending
      ? rejectMutation.variables
      : null;
  const actionError = approveMutation.error || rejectMutation.error;

  const handleApprove = (id: string) => {
    approveMutation.mutate(id);
  };

  const handleReject = (id: string, name: string) => {
    const confirmed = window.confirm(
      `Reject and delete "${name}"? This permanently removes the listing.`
    );
    if (!confirmed) return;
    rejectMutation.mutate(id);
  };

  return (
    <Page>
      <HeaderTitle>Submitted Businesses</HeaderTitle>
      {actionError ? (
        <ErrorText>
          {actionError instanceof Error ? actionError.message : 'Unable to update this listing.'}
        </ErrorText>
      ) : null}
      <TableCard>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Contact</th>
                <th>Website</th>
                <th>Address</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7}>Loading submitted businesses…</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7}>Error loading submitted businesses.</td>
                </tr>
              ) : businesses?.length ? (
                businesses.map((business) => {
                  const website = normalizeWebsite(business.website);
                  const isBusy = busyId === business.id;
                  return (
                    <tr key={business.id}>
                      <td>{business.name}</td>
                      <td>{business.description?.trim() || '-'}</td>
                      <td>
                        {business.phone || business.email
                          ? [business.phone, business.email].filter(Boolean).join(' · ')
                          : '-'}
                      </td>
                      <td>
                        {website ? (
                          <a href={website} target="_blank" rel="noreferrer">
                            {business.website}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{business.address?.trim() || '-'}</td>
                      <td>{formatSubmittedAt(business.created_at)}</td>
                      <td>
                        <Actions>
                          <ApproveButton
                            type="button"
                            disabled={Boolean(busyId)}
                            onClick={() => handleApprove(business.id)}
                          >
                            {isBusy && approveMutation.isPending ? 'Approving…' : 'Approve'}
                          </ApproveButton>
                          <DangerButton
                            type="button"
                            disabled={Boolean(busyId)}
                            onClick={() => handleReject(business.id, business.name)}
                          >
                            {isBusy && rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
                          </DangerButton>
                        </Actions>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>No submitted businesses.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      </TableCard>
    </Page>
  );
};

export default SubmittedBusinessesPage;
